import { ReactNode } from 'react';
import { Save } from 'react-feather';
import { useIntl } from 'react-intl';
import { Spinner } from 'reactstrap';
import './FormActions.scss';

interface Props {
  onSave?: () => void;
  onCancel?: () => void;
  saveType?: 'submit' | 'button';
  size?: 'sm' | 'md' | 'lg';
  /** 'footer' = barra sticky al fondo del sliding pane (divisor arriba, botones lg centrados) */
  variant?: 'inline' | 'footer';
  disabled?: boolean;
  /** Disable only the save button (e.g. invalid form) while keeping cancel active */
  saveDisabled?: boolean;
  /** Muestra spinner en Guardar y lo deshabilita (estado de guardado en curso) */
  loading?: boolean;
  /** Override del texto de Guardar (ej. "Agregar") */
  saveLabel?: ReactNode;
  /** Override del texto de Cancelar */
  cancelLabel?: ReactNode;
  /** Ocultar el botón de Guardar */
  showSave?: boolean;
  /** Ocultar el botón de Cancelar */
  showCancel?: boolean;
  /** id del botón Guardar (preservar selectores de tests) */
  saveId?: string;
  /** id del botón Cancelar */
  cancelId?: string;
  /** Botones adicionales renderizados entre Guardar y Cancelar */
  extraActions?: ReactNode;
  className?: string;
}

const ICON_SIZE: Record<string, number> = { sm: 11, md: 13, lg: 15 };

const FormActions = ({
  onSave,
  onCancel,
  saveType = 'submit',
  size = 'md',
  variant = 'inline',
  disabled = false,
  saveDisabled = false,
  loading = false,
  saveLabel,
  cancelLabel,
  showSave = true,
  showCancel = true,
  saveId,
  cancelId,
  extraActions,
  className = ''
}: Props) => {
  const intl = useIntl();

  // La variante footer siempre usa botones lg
  const effectiveSize = variant === 'footer' ? 'lg' : size;

  return (
    <div className={`form-actions form-actions--${effectiveSize} form-actions--${variant} ${className}`}>
      {showSave && (
        <button
          type={saveType}
          id={saveId}
          className="form-actions__save"
          onClick={onSave}
          disabled={disabled || saveDisabled || loading}
        >
          {loading ? (
            <Spinner color="white" size="sm" />
          ) : (
            <>
              <Save size={ICON_SIZE[effectiveSize]} />
              {saveLabel ?? intl.formatMessage({ id: 'common.save' })}
            </>
          )}
        </button>
      )}
      {extraActions}
      {showCancel && (
        <button
          type="button"
          id={cancelId}
          className="form-actions__cancel"
          onClick={onCancel}
          disabled={disabled}
        >
          {cancelLabel ?? intl.formatMessage({ id: 'common.cancel' })}
        </button>
      )}
    </div>
  );
};

export default FormActions;
